"use client";

import React, { useEffect, useState } from "react";
import { ListItemNode, ListNode } from "@lexical/list";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { Toolbar } from "./Toolbar";
import { ToolbarContext } from "./ToolbarContext";
import LexicalAutoLinkPlugin from "./AutoLink";
import FloatingLinkEditorPlugin from "./FloatingLinkEditor";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot } from "lexical";
import { TextNode } from "lexical"; // Or other relevant nodes

const EditorContent = ({ setComment, initialValue }) => {
  const [editor] = useLexicalComposerContext();
  const [isLinkEditMode, setIsLinkEditMode] = useState(false);
  const [floatingAnchorElem, setFloatingAnchorElem] = useState(null);
  const onRef = (_floatingAnchorElem) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    if (!initialValue) return;

    const decodeEntities = (encodedString) => {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = encodedString;
      return textarea.value;
    };

    const decodedString = decodeEntities(initialValue).trim();

    // If nothing remains, skip editor update
    if (!decodedString) return;

    editor.update(() => {
      const parser = new DOMParser();
      
      // Check if the content contains HTML tags
      const isHTML = /<[^>]*>/g.test(decodedString);
      
      let dom;
      if (isHTML) {
        // Parse as HTML
        dom = parser.parseFromString(decodedString, "text/html");
      } else {
        // Convert plain text to HTML paragraph
        const htmlContent = `<p>${decodedString}</p>`;
        dom = parser.parseFromString(htmlContent, "text/html");
      }
      
      const nodes = $generateNodesFromDOM(editor, dom);
      $getRoot().clear();

      nodes.forEach((n) => {
        $getRoot().append(n);
      });
    });
  }, [initialValue, editor]);

  return (
    <>
      <div className="w-full">
        <div className="flex flex-col items-start h-[280px] mx-auto relative ">
          <div className="flex flex-row justify-between items-center py-2 px-1 sm:px-3.5 w-full border border-b border-gray-200 rounded-t-lg bg-white">
            <Toolbar setIsLinkEditMode={setIsLinkEditMode} />
          </div>
          <div className="relative w-full h-[185px]">
            <RichTextPlugin
              ErrorBoundary={LexicalErrorBoundary}
              contentEditable={
                <div ref={onRef} className="h-full">
                  <ContentEditable className="w-full h-full p-4 overflow-auto bg-white border border-t-0 border-gray-200 rounded-b-lg focus:outline-none" />
                </div>
              }
              placeholder={
                <div className="text-gray-400 overflow-hidden absolute top-4 left-4 select-none inline-block pointer-events-none">
                  {`Enter your texts here`}
                </div>
              }
            />
            <OnChangePlugin
              onChange={(editorState, editor) => {
                editorState.read(() => {
                  let html = $generateHtmlFromNodes(editor);
                  
                  setComment(html);
                });
              }}
            />

            <ListPlugin />
            <HistoryPlugin />
            <LinkPlugin />
            <LexicalAutoLinkPlugin />

            {floatingAnchorElem && (
              <FloatingLinkEditorPlugin
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const LexicalEditor = ({ setComment, initialValue }) => {
  const initialConfig = {
    namespace: "MyEditor",
    onError: (error) => {
      console.log(error);
    },
    nodes: [
      ListItemNode,
      ListNode,
      HeadingNode,
      QuoteNode,
      CodeHighlightNode,
      CodeNode,
      LinkNode,
      AutoLinkNode,
      TextNode,
    ],
    theme: {
      link: "editor-link",
      code: "PlaygroundEditorTheme__code",
      text: {
        bold: "font-bold",
        italic: "italic",
        strikethrough: "line-through",
        underline: "editor-text-underline",
        code: "PlaygroundEditorTheme__code",
        capitalize: "PlaygroundEditorTheme__textCapitalize",
        highlight: "PlaygroundEditorTheme__textHighlight",
        lowercase: "PlaygroundEditorTheme__textLowercase",
        subscript: "PlaygroundEditorTheme__textSubscript",
        superscript: "PlaygroundEditorTheme__textSuperscript",
        uppercase: "PlaygroundEditorTheme__textUppercase",
      },
      list: {
        ul: "list-disc list-inside",
        ol: "list-decimal list-inside",
        checklist: "PlaygroundEditorTheme__checklist",
      },
      paragraph: "editor-paragraph",
      quote: "editor-quote",
      heading: {
        h1: "editor-h1",
        h2: "editor-h2",
        h3: "editor-h3",
        h4: "editor-h4",
        h5: "editor-h5",
        h6: "editor-h6",
      },
      codeHighlight: {
        number: "editor-number",
      },
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <ToolbarContext>
        <EditorContent setComment={setComment} initialValue={initialValue} />
      </ToolbarContext>
    </LexicalComposer>
  );
};

export default LexicalEditor;
